import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select.tsx";
import { queryKeys } from "@/services/queryKeyFactory.ts";
import { getCronJobs, getCronRuns, type CronRun, type CronRunStatus } from "@/services/cronService.ts";
import { activeLocale } from "@/utils/translation-helper.ts";

function formatDateTime(iso: string | null): string {
    if (!iso) return "—";
    const locale = activeLocale() === "fr" ? "fr-CA" : "en-CA";
    return new Date(iso).toLocaleString(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function formatDuration(ms: number | null): string {
    if (ms == null) return "—";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
}

function StatusBadge({ status }: { status: CronRunStatus }) {
    const { t } = useTranslation();
    if (status === "success") return <Badge variant="success">{t("scheduledJobs.statusSuccess")}</Badge>;
    if (status === "failed") return <Badge variant="destructive">{t("scheduledJobs.statusFailed")}</Badge>;
    return <Badge variant="secondary">{t("scheduledJobs.statusRunning")}</Badge>;
}

function SummaryCell({ run }: { run: CronRun }) {
    if (run.status === "failed" && run.error) {
        return <span className="text-status-danger break-words">{run.error}</span>;
    }
    if (run.summary && Object.keys(run.summary).length > 0) {
        return (
            <span className="text-text-2">
                {Object.entries(run.summary)
                    .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
                    .join(", ")}
            </span>
        );
    }
    return <span className="text-text-3">—</span>;
}

const ScheduledJobsPage = () => {
    const { t } = useTranslation();
    const [jobFilter, setJobFilter] = useState<string>("all");
    const [statusFilter, setStatusFilter] = useState<string>("all");

    const {
        data: jobs = [],
        isFetching: jobsFetching,
        isError: jobsError,
        refetch: refetchJobs,
    } = useQuery({
        queryKey: queryKeys.cron.jobs,
        queryFn: getCronJobs,
        refetchInterval: 30000,
    });

    const {
        data: runsData,
        isFetching: runsFetching,
        refetch: refetchRuns,
    } = useQuery({
        queryKey: queryKeys.cron.runs(jobFilter, statusFilter),
        queryFn: () =>
            getCronRuns({
                jobName: jobFilter === "all" ? undefined : jobFilter,
                status: statusFilter === "all" ? undefined : statusFilter,
                limit: 50,
            }),
        refetchInterval: 30000,
    });

    const runs = runsData?.runs ?? [];

    const onRefresh = () => {
        refetchJobs();
        refetchRuns();
    };

    return (
        <div className="space-y-8">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-0.5">
                    <h3 className="text-lg font-semibold text-text-1">{t("scheduledJobs.title")}</h3>
                    <p className="text-sm text-text-2">{t("scheduledJobs.description")}</p>
                </div>
                <Button variant="outline" size="sm" className="gap-2" onClick={onRefresh} disabled={jobsFetching || runsFetching}>
                    <RefreshCw className={`h-4 w-4 ${jobsFetching || runsFetching ? "animate-spin" : ""}`} />
                    {t("scheduledJobs.refresh")}
                </Button>
            </div>

            {/* Job cards */}
            <div className="grid gap-4 sm:grid-cols-2">
                {jobsError && <p className="text-sm text-status-danger">{t("scheduledJobs.loadError")}</p>}
                {jobsFetching && jobs.length === 0 && (
                    <>
                        <Skeleton className="h-40 w-full rounded-xl" />
                        <Skeleton className="h-40 w-full rounded-xl" />
                    </>
                )}
                {jobs.map((job) => (
                    <Card key={job.name} className="border border-border bg-bg-card">
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between gap-2">
                                <CardTitle className="text-base font-semibold text-text-1">{job.name}</CardTitle>
                                {job.lastRun ? <StatusBadge status={job.lastRun.status} /> : null}
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                            <p className="text-text-2">{job.description}</p>
                            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 pt-1">
                                <dt className="text-text-3">{t("scheduledJobs.schedule")}</dt>
                                <dd className="text-text-1"><code className="text-xs">{job.cronExpr}</code> · {job.timezone}</dd>
                                <dt className="text-text-3">{t("scheduledJobs.nextRun")}</dt>
                                <dd className="text-text-1">{formatDateTime(job.nextRunAt)}</dd>
                                <dt className="text-text-3">{t("scheduledJobs.lastRun")}</dt>
                                <dd className="text-text-1">
                                    {job.lastRun ? formatDateTime(job.lastRun.startedAt) : t("scheduledJobs.neverRun")}
                                </dd>
                            </dl>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Run history */}
            <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <h4 className="text-base font-semibold text-text-1">{t("scheduledJobs.recentRuns")}</h4>
                    <div className="flex flex-wrap items-center gap-2">
                        <Select value={jobFilter} onValueChange={setJobFilter}>
                            <SelectTrigger className="h-9 w-48 bg-bg-card border-border">
                                <SelectValue placeholder={t("scheduledJobs.allJobs")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t("scheduledJobs.allJobs")}</SelectItem>
                                {jobs.map((job) => (
                                    <SelectItem key={job.name} value={job.name}>
                                        {job.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="h-9 w-40 bg-bg-card border-border">
                                <SelectValue placeholder={t("scheduledJobs.allStatuses")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t("scheduledJobs.allStatuses")}</SelectItem>
                                <SelectItem value="success">{t("scheduledJobs.statusSuccess")}</SelectItem>
                                <SelectItem value="failed">{t("scheduledJobs.statusFailed")}</SelectItem>
                                <SelectItem value="running">{t("scheduledJobs.statusRunning")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-bg-elevated/40 text-text-2">
                            <tr>
                                <th className="px-4 py-2 font-medium">{t("scheduledJobs.job")}</th>
                                <th className="px-4 py-2 font-medium">{t("scheduledJobs.started")}</th>
                                <th className="px-4 py-2 font-medium">{t("scheduledJobs.duration")}</th>
                                <th className="px-4 py-2 font-medium">{t("scheduledJobs.status")}</th>
                                <th className="px-4 py-2 font-medium">{t("scheduledJobs.details")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.length === 0 && !runsFetching && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-6 text-center text-text-3">
                                        {t("scheduledJobs.noRuns")}
                                    </td>
                                </tr>
                            )}
                            {runs.map((run) => (
                                <tr key={run.id} className="border-t border-border align-top">
                                    <td className="px-4 py-2 text-text-1 whitespace-nowrap">{run.jobName}</td>
                                    <td className="px-4 py-2 text-text-2 whitespace-nowrap">{formatDateTime(run.startedAt)}</td>
                                    <td className="px-4 py-2 text-text-2 whitespace-nowrap">{formatDuration(run.durationMs)}</td>
                                    <td className="px-4 py-2"><StatusBadge status={run.status} /></td>
                                    <td className="px-4 py-2 max-w-md"><SummaryCell run={run} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ScheduledJobsPage;
